/**
 * Canonical totals calculation for quotes, invoices, and field jobs.
 *
 * Order of operations (matches Stripe, QuickBooks, Jobber):
 *   1. subtotal        = Σ(qty × unit_price)
 *   2. discount_amount = % of subtotal   (type='percentage')
 *                        OR fixed amount (type='fixed', clamped to subtotal)
 *                        OR 0            (type='none')
 *   3. tax_amount      = (subtotal − discount_amount) × tax_rate / 100
 *   4. total           = subtotal − discount_amount + tax_amount
 *
 * Discount is pre-tax: tax is owed on the price actually paid. This is the
 * default in every reference platform we benchmarked, and matches the prior
 * field_jobs implementation that this helper supersedes.
 *
 * Fixed-discount clamp: Math.min(discountValue, subtotal). Prevents a $500
 * discount on a $300 invoice from producing a negative total. The UI should
 * validate percentage ≤ 100 separately so percentage discounts don't need
 * the same clamp (a 100% discount lands exactly at subtotal).
 */

export type DiscountType = 'none' | 'percentage' | 'fixed'

// ─── Arc B Slice 2: per-line tiered / volume pricing ─────────────────────────
export type PricingType = 'flat' | 'tiered' | 'volume'

// An ordered price schedule. Bands are ascending by `upTo`; exactly ONE final
// tier has upTo=null meaning "and above". Only consulted when pricing_type is
// 'tiered' or 'volume'. e.g. [{upTo:10,price:5},{upTo:50,price:4},{upTo:null,price:3}].
export interface PricingTier {
  upTo: number | null
  price: number
}

export interface LineItemForTotals {
  qty: number | null | undefined
  unit_price: number | null | undefined
  // Slice 2 — optional; missing/'flat' (or empty tiers) → plain qty × unit_price.
  pricing_type?: PricingType | null
  tiers?: PricingTier[] | null
  // Slice 3 — optional per-line modifiers: total = base × (mult ?? 1) + (fee ?? 0).
  line_fee?: number | null
  price_multiplier?: number | null
}

// Malformed/empty tiers are treated as flat (never throw). A usable schedule has
// ≥1 well-formed {upTo, price} entry.
export function isValidTiers(tiers: PricingTier[] | null | undefined): tiers is PricingTier[] {
  return Array.isArray(tiers) && tiers.length > 0 &&
    tiers.every(t => t != null && (t.upTo === null || Number.isFinite(Number(t.upTo))) && Number.isFinite(Number(t.price)))
}

/**
 * The total for a single line, by pricing_type. The ONE place a line's price is
 * computed — every editor/total/email/portal site calls this so flat, tiered,
 * and volume stay consistent.
 *
 *   flat   → qty × unit_price.
 *   volume (all-units): the whole qty is priced at the band qty falls into —
 *            the first tier where (upTo == null || qty <= upTo); qty × that price.
 *   tiered (marginal): each band of units is priced at its own tier —
 *            Σ over bands of (units that fall in the band × band price).
 *
 * Worked examples (tiers [{upTo:10,price:5},{upTo:50,price:4},{upTo:null,price:3}]):
 *   tiered, qty 60 → 10×5 + 40×4 + 10×3 = 240
 *   volume, qty 60 → 60 in the null ("and above") band → 60×3 = 180
 *
 * Malformed/empty tiers, or pricing_type flat/missing → falls back to flat.
 */
// A display band for the portal/PDF breakdown: `units` priced at `price`.
// `volume` marks the single all-units band (a "volume rate" line).
export interface PricingBand {
  units: number
  price: number
  volume?: boolean
}

/**
 * The per-band breakdown for a tiered/volume line — the display companion to
 * lineTotal. Mirrors lineTotal's band math EXACTLY, so Σ(band.units × band.price)
 * === lineTotal(line) (the shown bands reconcile to the stored line total).
 *
 *   tiered → one band per reached tier (0-unit bands skipped), e.g.
 *            qty 60 over [{upTo:10,$5},{upTo:50,$4},{upTo:null,$3}]
 *            → [{units:10,price:5},{units:40,price:4},{units:10,price:3}].
 *   volume → the single applied band: [{units:qty, price:band, volume:true}].
 *
 * Returns [] for flat / missing pricing_type / invalid tiers / qty<=0 — callers
 * fall back to the plain "qty × unit_price" label (which matches the flat total).
 */
export function pricingBands(line: {
  qty: number | null | undefined
  pricing_type?: PricingType | null
  tiers?: PricingTier[] | null
}): PricingBand[] {
  const qty = Number(line.qty) || 0
  if (line.pricing_type !== 'tiered' && line.pricing_type !== 'volume') return []
  if (!isValidTiers(line.tiers) || qty <= 0) return []
  const tiers = line.tiers

  if (line.pricing_type === 'volume') {
    const band = tiers.find(t => t.upTo === null || qty <= Number(t.upTo)) ?? tiers[tiers.length - 1]
    return [{ units: qty, price: Number(band.price) || 0, volume: true }]
  }

  const bands: PricingBand[] = []
  let lower = 0
  for (const t of tiers) {
    const upper = t.upTo === null ? Infinity : Number(t.upTo)
    const units = Math.max(0, Math.min(qty, upper) - lower)
    if (units > 0) bands.push({ units, price: Number(t.price) || 0 })
    lower = upper
    if (qty <= upper) break
  }
  return bands
}

// The BASE line price by pricing_type (flat/tiered/volume), BEFORE Slice-3
// modifiers. lineTotal() wraps this with × multiplier + fee. Kept separate so
// pricingBands (the display breakdown) shares the exact base math.
function baseLineTotal(line: {
  qty: number | null | undefined
  unit_price: number | null | undefined
  pricing_type?: PricingType | null
  tiers?: PricingTier[] | null
}): number {
  const qty = Number(line.qty) || 0
  const unitPrice = Number(line.unit_price) || 0

  // flat (or malformed tiers) → plain qty × unit_price.
  if (line.pricing_type !== 'tiered' && line.pricing_type !== 'volume') return qty * unitPrice
  if (!isValidTiers(line.tiers)) return qty * unitPrice
  if (qty <= 0) return 0

  const tiers = line.tiers

  if (line.pricing_type === 'volume') {
    // All-units: find the band qty falls into; the whole qty at that price.
    const band = tiers.find(t => t.upTo === null || qty <= Number(t.upTo)) ?? tiers[tiers.length - 1]
    return qty * (Number(band.price) || 0)
  }

  // tiered (marginal): sum each band's units at its own price.
  let sum = 0
  let lower = 0
  for (const t of tiers) {
    const upper = t.upTo === null ? Infinity : Number(t.upTo)
    const unitsInBand = Math.max(0, Math.min(qty, upper) - lower)
    sum += unitsInBand * (Number(t.price) || 0)
    lower = upper
    if (qty <= upper) break // qty exhausted within this band
  }
  return sum
}

/**
 * Arc B Slice 3: per-line modifiers compose with the pricing_type base:
 *   total = base × (price_multiplier ?? 1) + (line_fee ?? 0)
 * where base = baseLineTotal (flat/tiered/volume). Guards: a non-positive /
 * NaN / null multiplier → 1; a null/NaN fee → 0 — so a line with no modifiers
 * is byte-identical to the base. Composes with ALL pricing types (a tiered line
 * can carry a trip fee + a surcharge).
 */
export function lineTotal(line: {
  qty: number | null | undefined
  unit_price: number | null | undefined
  pricing_type?: PricingType | null
  tiers?: PricingTier[] | null
  line_fee?: number | null
  price_multiplier?: number | null
}): number {
  const base = baseLineTotal(line)
  const mult = (line.price_multiplier == null || !(Number(line.price_multiplier) > 0)) ? 1 : Number(line.price_multiplier)
  const fee = Number(line.line_fee) || 0
  return base * mult + fee
}

export interface TotalsInput {
  lineItems: LineItemForTotals[]
  discountType: DiscountType
  discountValue: number | null | undefined
  // Percentage, e.g. 8.25 for 8.25%.
  taxRate: number | null | undefined
  // Optional invoice-level minimum charge (Arc B Slice 1). Floors the SUBTOTAL
  // pre discount/tax: effectiveSubtotal = max(subtotal, minimumCharge). NULL/
  // undefined = no floor (behavior unchanged). Optional + last so existing
  // callers that don't pass it are unaffected.
  minimumCharge?: number | null
}

export interface TotalsResult {
  subtotal: number
  discountAmount: number
  taxAmount: number
  total: number
}

export function computeTotals(input: TotalsInput): TotalsResult {
  // Slice 2: each line contributes lineTotal(line) (flat/tiered/volume), not a
  // hardcoded qty × unit_price. The minimum floor below still sits AFTER this sum.
  const lineSubtotal = input.lineItems.reduce((sum, item) => sum + lineTotal(item), 0)

  // Arc B Slice 1: minimum charge floors the SUBTOTAL pre discount/tax. The
  // returned subtotal IS the effectiveSubtotal, so the stored/displayed subtotal
  // (and the downstream discount/tax/total, and the portal which reads the stored
  // columns) all reflect the floor. NULL/undefined minimum → no floor.
  // NOTE: a percentage discount on a floored subtotal CAN pull `total` below
  // minimumCharge — intended: the minimum floors the list price (subtotal), then
  // normal discounting applies. ("total never below minimum" would be a different
  // spec.)
  const minimumCharge = input.minimumCharge
  const subtotal = (minimumCharge != null && minimumCharge > lineSubtotal)
    ? minimumCharge
    : lineSubtotal

  const discountValue = Number(input.discountValue) || 0
  const discountAmount =
    input.discountType === 'percentage' ? subtotal * (discountValue / 100)
    : input.discountType === 'fixed'    ? Math.min(discountValue, subtotal)
    : 0

  const taxRate = Number(input.taxRate) || 0
  const taxAmount = (subtotal - discountAmount) * (taxRate / 100)

  const total = subtotal - discountAmount + taxAmount

  return {
    subtotal:       roundCents(subtotal),
    discountAmount: roundCents(discountAmount),
    taxAmount:      roundCents(taxAmount),
    total:          roundCents(total),
  }
}

function roundCents(n: number): number {
  return Math.round(n * 100) / 100
}
